/**
 * Hook for managing application lock state, takeover logic, and user lookup.
 * Uses useDgaApi internally for lock operations (DRY).
 */

import { useCallback, useState } from "react";
import { useDgaApi } from "./useDgaApi";
import { useEmployeeCache } from "./useEmployeeCache";

interface LockedByUser {
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface UseApplicationLockReturn {
  isLocked: boolean;
  lockedByUserId: string | null;
  lockedByUser: LockedByUser | null;
  isLoadingUser: boolean;
  lockApplication: (appId: string) => Promise<void>;
  unlockApplication: (appId: string) => Promise<void>;
  takeOverLock: (appId: string) => Promise<void>;
  refreshLockState: () => Promise<LockedByUser | null>;
}

export function useApplicationLock(
  appId: string | undefined,
  dealerId: string | undefined,
): UseApplicationLockReturn {
  const {
    lockApplication: apiLock,
    unlockApplication: apiUnlock,
    loadApplication,
  } = useDgaApi();
  const { getEmployeeUser } = useEmployeeCache(dealerId);

  const [isLocked, setIsLocked] = useState(false);
  const [lockedByUserId, setLockedByUserId] = useState<string | null>(null);
  const [lockedByUser, setLockedByUser] = useState<LockedByUser | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(false);

  // Lock application (normal lock)
  const lockApplication = useCallback(
    async (appId: string) => {
      await apiLock(appId);
    },
    [apiLock],
  );

  // Unlock application
  const unlockApplication = useCallback(
    async (appId: string) => {
      await apiUnlock(appId);
    },
    [apiUnlock],
  );

  // Take over lock (force lock)
  const takeOverLock = useCallback(
    async (appId: string) => {
      await apiLock(appId, true); // force = true
    },
    [apiLock],
  );

  // Refresh lock state from API
  const refreshLockState =
    useCallback(async (): Promise<LockedByUser | null> => {
      if (!appId) return null;

      try {
        const result = await loadApplication(appId);
        const lockedByUserId = result.application.locked_by_user_id ?? null;

        setIsLocked(!!lockedByUserId);
        setLockedByUserId(lockedByUserId);

        if (lockedByUserId) {
          setIsLoadingUser(true);
          const user = getEmployeeUser(lockedByUserId);
          setLockedByUser(user);
          setIsLoadingUser(false);
          return user;
        } else {
          setLockedByUser(null);
          return null;
        }
      } catch (error) {
        console.error(
          "[useApplicationLock] Failed to refresh lock state:",
          error,
        );
        throw error;
      }
    }, [appId, loadApplication, getEmployeeUser]);

  return {
    isLocked,
    lockedByUserId,
    lockedByUser,
    isLoadingUser,
    lockApplication,
    unlockApplication,
    takeOverLock,
    refreshLockState,
  };
}
